/**
 * InfoIconButton Component - Tappable info icon for mobile-friendly tooltips
 * Shows a small info icon that displays a tooltip on tap (mobile) or hover (desktop)
 */

import React, { useState } from 'react';
import { IconButton, Popover, Typography, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colors } from '../styles/styleConstants';

const SHOW_HINTS_KEY = 'clutterlogix_show_hints';

// Helper to check if hints are enabled
export const areHintsEnabled = () => {
  const saved = localStorage.getItem(SHOW_HINTS_KEY);
  // Default to true if not set
  return saved === null ? true : saved === 'true';
};

// Helper to toggle hints
export const setHintsEnabled = (enabled) => {
  localStorage.setItem(SHOW_HINTS_KEY, enabled.toString());
};

const InfoIconButton = ({ hint, size = 'small', color = 'inherit' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showHints] = useState(areHintsEnabled());

  // Don't render if hints are disabled
  if (!showHints) {
    return null;
  }

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size={size}
        onClick={handleClick}
        sx={{
          p: 0.25,
          ml: 0.5,
          color: color === 'light' ? 'rgba(255,255,255,0.6)' : colors.mediumSlateBlue,
          '&:hover': {
            color: color === 'light' ? 'rgba(255,255,255,0.9)' : colors.actionBlue,
            backgroundColor: 'transparent'
          }
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: size === 'small' ? 16 : 20 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              maxWidth: 220,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }
          }
        }}
      >
        <Typography variant="body2" sx={{ color: colors.charcoal, fontSize: '0.85rem' }}>
          {hint}
        </Typography>
      </Popover>
    </>
  );
};

export default InfoIconButton;
